import React, { useEffect, useState } from "react";
import { IconX } from "./icons";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const DISMISSED_KEY = "checkthelineup:install:dismissed:v1";
const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 7; // a week

function dismissedRecently(): boolean {
  if (typeof localStorage === "undefined") return false;
  const raw = localStorage.getItem(DISMISSED_KEY);
  if (!raw) return false;
  const at = Number(raw);
  if (!Number.isFinite(at)) return false;
  return Date.now() - at < DISMISS_COOLDOWN_MS;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS
  if ((window.navigator as unknown as { standalone?: boolean }).standalone) return true;
  // Everyone else
  return window.matchMedia?.("(display-mode: standalone)")?.matches ?? false;
}

function detectPlatform(): "ios" | "android" | "desktop" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

function isCapacitor(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
  return !!w.Capacitor?.isNativePlatform?.();
}

/**
 * Contextual "Install CheckTheLineUp" prompt:
 *   - Chrome / Edge / Android Chrome: shown when the browser fires
 *     beforeinstallprompt. Tapping it invokes the native install flow.
 *   - iOS Safari: shown with Share → Add to Home Screen instructions
 *     since iOS never fires beforeinstallprompt.
 *   - Hidden entirely inside Capacitor or when the app is already
 *     running standalone.
 */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android" | "desktop" | "unknown">("unknown");
  const [showIosInstructions, setShowIosInstructions] = useState(false);

  useEffect(() => {
    if (isCapacitor() || isStandalone() || dismissedRecently()) return;
    const p = detectPlatform();
    setPlatform(p);

    // iOS: surface the prompt after a brief delay so the user sees it once
    // the first page has settled.
    if (p === "ios") {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    const onInstalled = () => setVisible(false);

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosInstructions(false);
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const install = async () => {
    if (platform === "ios") {
      setShowIosInstructions(true);
      return;
    }
    if (!deferred) return;
    try {
      await deferred.prompt();
      const r = await deferred.userChoice;
      if (r.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
    } finally {
      setDeferred(null);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div className="install-bar" role="dialog" aria-label="Install CheckTheLineUp" data-testid="install-prompt">
        <div className="install-bar-icon" aria-hidden="true">
          <img src="/icons/icon-192.png" alt="" width={36} height={36} />
        </div>
        <div className="install-bar-body">
          <strong>Install CheckTheLineUp</strong>
          <span>Offline-ready, no browser chrome, tap to launch.</span>
        </div>
        <button onClick={install} className="install-bar-cta" data-testid="install-cta">
          Install
        </button>
        <button onClick={dismiss} className="install-bar-close" aria-label="Dismiss" data-testid="install-dismiss">
          <IconX />
        </button>
      </div>

      {showIosInstructions && (
        <div
          className="sheet-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={e => {
            if (e.target === e.currentTarget) setShowIosInstructions(false);
          }}
        >
          <div className="sheet" style={{ maxHeight: "70vh" }}>
            <div className="sheet-handle" />
            <div className="sheet-body" style={{ padding: "4px 22px 28px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setShowIosInstructions(false)}
                  aria-label="Close"
                  style={{ padding: 4 }}
                >
                  <IconX />
                </button>
              </div>
              <span className="sub">INSTALL ON IOS</span>
              <h1>Add to Home Screen</h1>
              <ol
                style={{
                  color: "var(--ink-dim)",
                  lineHeight: 1.6,
                  fontSize: 15,
                  paddingLeft: 20,
                  marginTop: 14,
                }}
              >
                <li>
                  Tap the{" "}
                  <strong style={{ color: "var(--ink)" }}>Share</strong> button
                  at the bottom of Safari (the square with the up-arrow).
                </li>
                <li style={{ marginTop: 10 }}>
                  Scroll and tap{" "}
                  <strong style={{ color: "var(--ink)" }}>"Add to Home Screen"</strong>.
                </li>
                <li style={{ marginTop: 10 }}>
                  Tap <strong style={{ color: "var(--ink)" }}>Add</strong> in
                  the top right.
                </li>
              </ol>
              <p
                style={{
                  color: "var(--ink-faint)",
                  fontSize: 13,
                  marginTop: 18,
                  lineHeight: 1.5,
                }}
              >
                The app installs as a standalone icon on your home screen —
                opens instantly, works offline once you've been online for
                the first load, and dodges all the Safari chrome.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
